/**
 * Mandate Question Builder — Admin library of bot questions triggered by
 * campaign mandates. Session 1 of the WhatsApp Bot Engine.
 *
 * Route: /admin/mandate-questions   (admin, CM, AE can view; admin-only writes)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Bot, Plus, Edit2, Trash2, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  personal: { label: 'Personal',  color: '#0055FF' },
  mandate:  { label: 'Mandate',   color: '#FF451A' },
  approval: { label: 'Approval',  color: '#00C972' },
};

const EMPTY_FORM = {
  mandate_type: '',
  display_name: '',
  mandate_keywords: [],
  suggested_question: '',
  question_category: 'mandate',
  requires_address: false,
  requires_cost_confirmation: false,
};

export const MandateQuestionBuilder = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [kwInput, setKwInput] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_URL}/api/admin/mandate-question-templates`,
        { withCredentials: true },
      );
      setTemplates(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setKwInput('');
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      mandate_type: t.mandate_type || '',
      display_name: t.display_name || '',
      mandate_keywords: [...(t.mandate_keywords || [])],
      suggested_question: t.suggested_question || '',
      question_category: t.question_category || 'mandate',
      requires_address: !!t.requires_address,
      requires_cost_confirmation: !!t.requires_cost_confirmation,
    });
    setKwInput('');
    setDialogOpen(true);
  };

  const addKeyword = () => {
    const v = kwInput.trim();
    if (!v) return;
    if (form.mandate_keywords.includes(v)) { setKwInput(''); return; }
    setForm((f) => ({ ...f, mandate_keywords: [...f.mandate_keywords, v] }));
    setKwInput('');
  };

  const removeKeyword = (kw) => {
    setForm((f) => ({ ...f, mandate_keywords: f.mandate_keywords.filter((k) => k !== kw) }));
  };

  const save = async () => {
    if (!form.mandate_type.trim() || !form.display_name.trim() || !form.suggested_question.trim()) {
      toast.error('Type, name and question are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(
          `${API_URL}/api/admin/mandate-question-templates/${editingId}`,
          form,
          { withCredentials: true },
        );
        toast.success('Template updated');
      } else {
        await axios.post(
          `${API_URL}/api/admin/mandate-question-templates`,
          form,
          { withCredentials: true },
        );
        toast.success('Template created');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.display_name}"?`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/mandate-question-templates/${t.id}`,
        { withCredentials: true },
      );
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="mandate-question-builder-page">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
                <Bot className="text-[#FF451A]" size={32} /> Mandate Question Templates
              </h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">
                Define questions the bot asks creators based on campaign mandates.
              </p>
            </div>
            {isAdmin && (
              <Button
                onClick={openCreate}
                className="bg-[#FF451A] text-white rounded-full px-4 py-2 hover:bg-[#E63E17] flex items-center gap-1.5"
                data-testid="add-template-button"
              >
                <Plus size={14} /> Add New Template
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-16 text-[#6B6B6B] dark:text-zinc-400 flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Loading templates…
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-black/[0.10] dark:border-white/10 rounded-2xl">
              <Bot size={40} className="mx-auto text-[#FF451A]/60 mb-3" />
              <div className="text-[#0F0F0F] dark:text-white font-semibold">No templates yet</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="template-grid">
              {templates.map((t) => {
                const cat = CATEGORY_META[t.question_category] || CATEGORY_META.mandate;
                return (
                  <div
                    key={t.id}
                    className="bg-white dark:bg-[#131518] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5"
                    data-testid={`template-card-${t.mandate_type}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white truncate">
                            {t.display_name || t.mandate_type}
                          </h3>
                          <span
                            className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider border"
                            style={{ color: cat.color, borderColor: `${cat.color}55`, backgroundColor: `${cat.color}14` }}
                          >
                            {cat.label}
                          </span>
                        </div>
                        <div className="text-[11px] font-mono text-[#6B6B6B] dark:text-zinc-500 mt-0.5">
                          {t.mandate_type}
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-1 shrink-0">
                          <button
                            onClick={() => openEdit(t)}
                            className="p-1.5 text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF451A] transition-colors"
                            data-testid={`edit-template-${t.mandate_type}`}
                          >
                            <Edit2 size={13} />
                          </button>
                          <button
                            onClick={() => remove(t)}
                            className="p-1.5 text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30] transition-colors"
                            data-testid={`delete-template-${t.mandate_type}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-1.5 mb-3 min-h-[22px]">
                      {(t.mandate_keywords || []).length === 0 ? (
                        <span className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 italic">No keywords · always runs when added manually</span>
                      ) : t.mandate_keywords.map((k) => (
                        <span
                          key={k}
                          className="px-2 py-0.5 rounded-full text-[10px] bg-zinc-100 dark:bg-white/[0.06] text-[#6B6B6B] dark:text-zinc-300 border border-black/[0.06] dark:border-white/10"
                        >
                          {k}
                        </span>
                      ))}
                    </div>

                    <div className="text-sm text-[#0F0F0F] dark:text-white bg-zinc-50 dark:bg-white/[0.03] border border-black/[0.05] dark:border-white/5 rounded-xl p-3 leading-relaxed">
                      {t.suggested_question}
                    </div>

                    <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-[#6B6B6B] dark:text-zinc-500 font-mono">
                      {t.requires_address && (
                        <span className="inline-flex items-center gap-1 text-[#FF451A]">↳ collects address</span>
                      )}
                      {t.requires_cost_confirmation && (
                        <span className="inline-flex items-center gap-1 text-[#00C972]">↳ confirms cost</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Template' : 'New Template'}</DialogTitle>
            <DialogDescription className="text-sm text-[#6B6B6B] dark:text-zinc-400">
              Define a bot question triggered by a campaign mandate.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <Field label="Mandate Type (slug) *">
              <Input
                value={form.mandate_type}
                onChange={(e) => setForm({ ...form, mandate_type: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                placeholder="outdoor_shoot"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="template-type-input"
                disabled={!!editingId}
              />
            </Field>
            <Field label="Display Name *">
              <Input
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder="Outdoor Shoot"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="template-name-input"
              />
            </Field>
            <Field label="Trigger Keywords">
              <div className="flex gap-1.5 mb-2">
                <Input
                  value={kwInput}
                  onChange={(e) => setKwInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                  placeholder="Type a keyword, press Enter"
                  className="rounded-xl text-sm bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                  data-testid="template-keyword-input"
                />
                <Button
                  type="button"
                  onClick={addKeyword}
                  className="bg-[#FF451A]/10 text-[#FF451A] hover:bg-[#FF451A]/20 rounded-lg px-3 text-xs border border-[#FF451A]/30"
                  data-testid="template-keyword-add"
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5 min-h-[24px]">
                {form.mandate_keywords.map((k) => (
                  <span
                    key={k}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-zinc-100 dark:bg-white/[0.06] text-[#0F0F0F] dark:text-white border border-black/[0.08] dark:border-white/10"
                  >
                    {k}
                    <button type="button" onClick={() => removeKeyword(k)} className="hover:text-[#FF3B30]">
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            </Field>
            <Field label="Suggested Question *">
              <textarea
                value={form.suggested_question}
                onChange={(e) => setForm({ ...form, suggested_question: e.target.value })}
                rows={4}
                placeholder="Are you comfortable with an outdoor shoot? What location works for you?"
                className="w-full rounded-xl text-sm p-3 outline-none focus:border-[#FF451A]/40 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="template-question-input"
              />
              <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 mt-1 font-mono">
                Placeholders supported: {'{{database_rate}}, {{deliverables}}, {{go_live_date}}'}
              </div>
            </Field>
            <Field label="Question Category">
              <select
                value={form.question_category}
                onChange={(e) => setForm({ ...form, question_category: e.target.value })}
                className="w-full rounded-xl text-sm px-3 py-2 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="template-category-select"
              >
                <option value="personal">Personal</option>
                <option value="mandate">Mandate</option>
                <option value="approval">Approval</option>
              </select>
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <Toggle
                label="Requires Address"
                checked={form.requires_address}
                onChange={(v) => setForm({ ...form, requires_address: v })}
                testid="template-requires-address"
              />
              <Toggle
                label="Requires Cost Confirmation"
                checked={form.requires_cost_confirmation}
                onChange={(v) => setForm({ ...form, requires_cost_confirmation: v })}
                testid="template-requires-cost"
              />
            </div>
          </div>

          <DialogFooter className="mt-4 gap-2">
            <Button
              type="button"
              onClick={() => setDialogOpen(false)}
              className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded-full px-4"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#FF451A] text-white rounded-full px-4 hover:bg-[#E63E17] disabled:opacity-40"
              data-testid="template-save-button"
            >
              {saving ? '…' : editingId ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-[0.08em] block mb-1.5 font-mono text-[#0F0F0F] dark:text-white">{label}</label>
    {children}
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`flex items-center justify-between gap-3 px-3 py-2 rounded-xl border text-xs font-medium transition-colors ${
      checked
        ? 'border-[#FF451A]/40 bg-[#FF451A]/10 text-[#FF451A]'
        : 'border-black/[0.10] dark:border-white/10 bg-white dark:bg-[#0A0B0D] text-[#6B6B6B] dark:text-zinc-400'
    }`}
    data-testid={testid}
  >
    <span>{label}</span>
    <span
      className={`w-8 h-4 rounded-full relative transition-colors ${
        checked ? 'bg-[#FF451A]' : 'bg-black/10 dark:bg-white/10'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </span>
  </button>
);

export default MandateQuestionBuilder;
